import { Icon, IconPack } from '@healingpaper/cell'
import Link from 'next/link'
import styled from 'styled-components'

import CheetahIcon, { CheetahIconPack } from '@/components/atomic/atoms/CheetahIcon'
import { generateTagId } from '@/utils/test-automation'
import { useLocales } from '@/shared/hooks/useLocales'
import { isProd } from '@/shared/utils'

export type GlobalNavBarTabKey = 'home' | 'hospital' | 'event' | 'community' | 'column'
interface Props {
  selectedGnbTabKey?: GlobalNavBarTabKey
  text: {
    home: string
    homeLink: string
    hospital: string
    hospitalLink: string
    event: string
    eventLink: string
    column: string
    columnLink: string
    community: string
    communityLink: string
  }
  onclick?: (key: GlobalNavBarTabKey) => void
}
const GlobalNavBar = ({ selectedGnbTabKey, text, onclick }: Props): JSX.Element => {
  const { isLocale } = useLocales()

  const isPostTabVisible = !isLocale.US && !isLocale.TH && !isLocale.ZH_HANS && !isLocale.ZH_HANT

  return (
    <StyledContainer>
      <StyledNav>
        <StyledTabList>
          <StyledTab>
            <Link href="/" id={generateTagId('all', 'tab', 'link', 'moveTo', 'home')}>
              <StyledText $selected={selectedGnbTabKey === 'home'} onClick={() => onclick?.('home')}>
                {selectedGnbTabKey === 'home' ? (
                  <Icon name={IconPack.NAVBAR_HOME_ON} width={24} height={24} />
                ) : (
                  <Icon name={IconPack.NAVBAR_HOME_OFF} width={24} height={24} />
                )}
                {text.home}
              </StyledText>
            </Link>
          </StyledTab>
          <StyledTab>
            <Link href="/hospitals" id={generateTagId('all', 'tab', 'link', 'moveTo', 'hospital')}>
              <StyledText $selected={selectedGnbTabKey === 'hospital'} onClick={() => onclick?.('hospital')}>
                {selectedGnbTabKey === 'hospital' ? (
                  <Icon name={IconPack.NAVBAR_HOSPITAL_ON} width={24} height={24} />
                ) : (
                  <Icon name={IconPack.NAVBAR_HOSPITAL_OFF} width={24} height={24} />
                )}
                {text.hospital}
              </StyledText>
            </Link>
          </StyledTab>
          <StyledTab>
            <Link href="/events" id={generateTagId('all', 'tab', 'link', 'moveTo', 'event')}>
              <StyledText $selected={selectedGnbTabKey === 'event'} onClick={() => onclick?.('event')}>
                {selectedGnbTabKey === 'event' ? (
                  <Icon name={IconPack.NAVBAR_EVENT_ON} width={24} height={24} />
                ) : (
                  <Icon name={IconPack.NAVBAR_EVENT_OFF} width={24} height={24} />
                )}
                {text.event}
              </StyledText>
            </Link>
          </StyledTab>
          <StyledTab>
            <Link href="/community" id={generateTagId('all', 'tab', 'link', 'moveTo', 'community')}>
              <StyledText $selected={selectedGnbTabKey === 'community'} onClick={() => onclick?.('community')}>
                {selectedGnbTabKey === 'community' ? (
                  <img src="/assets/icon/navbarCommunityOn.svg" alt="community icon" />
                ) : (
                  <img src="/assets/icon/navbarCommunityOff.svg" alt="community icon" />
                )}
                {text.community}
              </StyledText>
            </Link>
          </StyledTab>
          {isPostTabVisible && (
            <StyledTab>
              <Link href="/posts" id={generateTagId('all', 'tab', 'link', 'moveTo', 'column')}>
                <StyledText $selected={selectedGnbTabKey === 'column'} onClick={() => onclick?.('column')}>
                  {selectedGnbTabKey === 'column' ? (
                    <CheetahIcon name={CheetahIconPack.NAVBAR_COLUMN_ON} width={24} height={24} />
                  ) : (
                    <CheetahIcon name={CheetahIconPack.NAVBAR_COLUMN_OFF} width={24} height={24} />
                  )}
                  {text.column}
                </StyledText>
              </Link>
            </StyledTab>
          )}
        </StyledTabList>
      </StyledNav>
    </StyledContainer>
  )
}
const StyledContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  height: 65px;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.gray400};
`

const StyledNav = styled.nav`
  height: 100%;
`

const StyledTabList = styled.ul`
  list-style: none;
  height: 100%;
  padding: 0px;
  margin: 0px;
  display: flex;
`

const StyledTab = styled.li`
  flex: 1;
  cursor: pointer;
  a {
    text-decoration: none;
  }
`

const StyledText = styled.span<{ $selected: boolean }>`
  color: ${({ theme, $selected }) => ($selected ? theme.colors.orange : theme.colors.gray800)};
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.typography.caption1};
  svg,
  img {
    margin-bottom: 4px;
  }
`

export default GlobalNavBar
